/**
 * Build a printable "Mr. Berwind — Chat Transcript" document.
 *
 * Layout, fonts and colours mirror the approved Action Tour Guide template:
 *   DM Sans (body) + DM Mono (labels), gold #c08a1e accent, blue #3d6db5 user label,
 *   tinted #f7f9fc bands behind user turns, A4 with 33pt side margins.
 *
 * Rendered as HTML and handed to the browser's print dialog so the text stays
 * selectable/searchable in the resulting PDF (no rasterising, no extra deps).
 */

const AVATAR_LABEL = 'MR. BERWIND';

const escapeHtml = (value) =>
  String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const pad2 = (n) => String(n).padStart(2, '0');

/** "9 Jul, 06:51" */
function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const month = d.toLocaleString('en-US', { month: 'short' });
  return `${d.getDate()} ${month}, ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** "07:09" */
function formatClock(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** "9 JUL, 07:09" — used by the REVIEW label. */
function formatStampUpper(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const month = d.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  return `${d.getDate()} ${month}, ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** "9 July 2026" — footer date. */
function formatFooterDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${d.toLocaleString('en-US', { month: 'long' })} ${d.getFullYear()}`;
}

/** "17m 16s" */
function formatDuration(startedAt, endedAt) {
  if (!startedAt || !endedAt) return '';
  const a = new Date(startedAt).getTime();
  const b = new Date(endedAt).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b <= a) return '';
  const total = Math.round((b - a) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatUsd(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  return `US$${n.toFixed(2)}`;
}

/** "· 34.6 credits @ $99/1k" */
function formatCreditsSuffix(costBreakdown) {
  const b = costBreakdown || {};
  if (b.creditsUsed == null || b.pricePerThousandCredits == null) return '';
  return `· ${b.creditsUsed} credits @ $${b.pricePerThousandCredits}/1k`;
}

/** Feedback name, else the email's local part, else "Guest" — shown as the user's label. */
function resolveUserLabel(session, feedback) {
  const name = String(feedback?.name || '').trim();
  if (name && !name.includes('@')) return name.toUpperCase();
  const email = String(session?.userEmail || '').trim();
  if (email) return email.split('@')[0].toUpperCase();
  return 'GUEST';
}

function renderStars(rating) {
  const r = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  let out = '';
  for (let i = 1; i <= 5; i += 1) {
    out += i <= r
      ? '<span class="star on">★</span>'
      : '<span class="star off">☆</span>';
  }
  return out;
}

function metaBox(label, valueHtml) {
  return `<div class="box"><div class="box-label">${escapeHtml(label)}</div><div class="box-value">${valueHtml}</div></div>`;
}

function renderTranscript(item, isLast) {
  const session = item?.session || {};
  const feedback = item?.feedback || null;
  const messages = Array.isArray(item?.messages) ? item.messages : [];

  const email = String(session.userEmail || '').trim();
  const userLabel = resolveUserLabel(session, feedback);
  const sessionId = String(session.sessionId || '');
  const shortId = sessionId.split('-')[0] || sessionId;

  const started = formatDateTime(session.startedAt);
  const endedClock = formatClock(session.endedAt);
  const duration = formatDuration(session.startedAt, session.endedAt);
  const creditsSuffix = formatCreditsSuffix(session.costBreakdown);

  // ── Meta boxes ──────────────────────────────────────────────────────────
  const boxes = [];
  boxes.push(metaBox('USER', `<span class="mono">${escapeHtml(email || 'Unknown user')}</span>`));
  boxes.push(metaBox(
    'SESSION',
    `<span class="strong">${escapeHtml(started)}</span>` +
    (endedClock ? ` <span class="arrow">→</span> <span class="strong">${escapeHtml(endedClock)}</span>` : '') +
    (duration ? ` <span class="muted">(${escapeHtml(duration)})</span>` : '')
  ));
  boxes.push(metaBox(
    'COST',
    `<span class="strong">${escapeHtml(formatUsd(session.sessionCostUsd))}</span>` +
    (creditsSuffix ? ` <span class="muted">${escapeHtml(creditsSuffix)}</span>` : '')
  ));
  boxes.push(metaBox('SESSION ID', `<span class="mono wrap">${escapeHtml(sessionId)}</span>`));
  if (feedback) {
    const stamp = formatStampUpper(feedback.createdAt || feedback.created_at);
    boxes.push(metaBox(
      `REVIEW${stamp ? ` · ${stamp}` : ''}`,
      `<span class="stars">${renderStars(feedback.rating)}</span>`
    ));
  }

  // ── Turns ───────────────────────────────────────────────────────────────
  const rows = messages.map((m) => {
    const isUser = m.role === 'user';
    const text = escapeHtml(m.content || '');
    if (isUser) {
      return (
        '<div class="turn user">' +
          '<div class="who">' +
            `<div class="who-name user-name">${escapeHtml(userLabel)}</div>` +
            (email ? `<div class="who-email">(${escapeHtml(email)})</div>` : '') +
          '</div>' +
          `<div class="text user-text">${text}</div>` +
        '</div>'
      );
    }
    return (
      '<div class="turn bot">' +
        `<div class="who"><div class="who-name bot-name">${AVATAR_LABEL}</div></div>` +
        `<div class="text">${text}</div>` +
      '</div>'
    );
  }).join('');

  const footerDate = formatFooterDate(session.startedAt);

  return (
    `<section class="sheet${isLast ? '' : ' break'}">` +
      '<div class="eyebrow">ACTION TOUR GUIDE &middot; VOICE CHARACTER SESSION</div>' +
      '<h1 class="title"><span class="title-dark">Mr. Berwind</span> <span class="title-gold">&mdash; Chat Transcript</span></h1>' +
      `<div class="boxes">${boxes.join('')}</div>` +
      '<div class="divider"><span>TRANSCRIPT</span></div>' +
      `<div class="turns">${rows || '<div class="empty">No messages recorded for this session.</div>'}</div>` +
      `<div class="doc-footer">Action Tour Guide &middot; Mr. Berwind Voice Chat Session ${escapeHtml(shortId)}${footerDate ? ` &middot; ${escapeHtml(footerDate)}` : ''}</div>` +
    '</section>'
  );
}

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;700&display=swap');

@page { size: A4; margin: 34pt 30pt 34pt 30pt; }

* { box-sizing: border-box; }
/* Always render light: never let the viewer's dark mode invert the transcript. */
html { color-scheme: light; background: #ffffff; }
html, body { margin: 0; padding: 0; }
body {
  font-family: 'DM Sans', -apple-system, 'Segoe UI', Arial, sans-serif;
  color: #1e2430;
  background: #ffffff;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* On screen, keep everything white and lay the content out like the printed page. */
@media screen {
  body { background: #ffffff; }
  .sheet {
    background: #ffffff;
    width: 794px;
    max-width: 100%;
    margin: 0 auto;
    padding: 46px 40px 56px;
  }
}

.sheet { padding: 0; background: #ffffff; }
.sheet.break { page-break-after: always; break-after: page; }

.eyebrow {
  font-family: 'DM Mono', ui-monospace, monospace;
  font-size: 7.4pt;
  letter-spacing: 0.28em;
  color: #8a93a3;
  text-transform: uppercase;
  margin-bottom: 7pt;
}

.title {
  margin: 0 0 14pt;
  font-size: 25pt;
  line-height: 1.1;
  font-weight: 700;
  font-stretch: condensed;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  font-family: 'DM Sans', Impact, 'Arial Narrow', sans-serif;
}
.title-dark { color: #12161f; }
.title-gold { color: #c08a1e; }

/* Meta boxes */
.boxes { display: flex; gap: 7pt; margin-bottom: 14pt; align-items: stretch; }
.box {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  border: 0.7pt solid #e8ebf0;
  background: #fbfcfd;
  border-radius: 4pt;
  padding: 7pt 8pt;
}
.box-label {
  font-family: 'DM Mono', ui-monospace, monospace;
  font-size: 6.2pt;
  letter-spacing: 0.14em;
  color: #8a93a3;
  text-transform: uppercase;
  margin-bottom: 4pt;
  /* Wrap rather than clip: labels like "REVIEW · 9 JUL, 07:09" must stay complete. */
  overflow-wrap: anywhere;
}
.box-value { font-size: 8.2pt; color: #12161f; line-height: 1.4; }
.box-value .strong { font-weight: 500; }
.box-value .muted { color: #8a93a3; font-weight: 400; }
.box-value .arrow { color: #12161f; }
/* Long emails/ids must wrap inside their box instead of running past the edge. */
.mono {
  font-family: 'DM Mono', ui-monospace, monospace;
  font-size: 7pt;
  font-weight: 500;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.mono.wrap { word-break: break-all; }
.stars { font-size: 10pt; letter-spacing: 0.08em; }
.star.on { color: #12161f; }
.star.off { color: #c8cdd6; }

/* TRANSCRIPT divider */
.divider { display: flex; align-items: center; gap: 10pt; margin: 0 0 8pt; }
.divider::before, .divider::after { content: ''; flex: 1; height: 0.7pt; background: #e8ebf0; }
.divider span {
  font-family: 'DM Mono', ui-monospace, monospace;
  font-size: 6.8pt;
  letter-spacing: 0.34em;
  color: #8a93a3;
}

/* Turns */
.turn {
  display: grid;
  grid-template-columns: 122pt 1fr;
  gap: 10pt;
  padding: 7pt 5pt;
  page-break-inside: avoid;
  break-inside: avoid;
}
.turn.user {
  background: #f7f9fc;
  border-top: 0.7pt solid #f0f2f5;
  border-bottom: 0.7pt solid #f0f2f5;
}
.who-name {
  font-family: 'DM Mono', ui-monospace, monospace;
  font-size: 7pt;
  font-weight: 500;
  letter-spacing: 0.1em;
}
.bot-name { color: #c08a1e; }
.user-name { color: #3d6db5; }
.who-email {
  font-family: 'DM Mono', ui-monospace, monospace;
  font-size: 6pt;
  font-weight: 500;
  color: #a7aeba;
  margin-top: 2pt;
  word-break: break-all;
}
.text { font-size: 8.4pt; line-height: 1.55; color: #1e2430; white-space: pre-wrap; }
.user-text { font-weight: 500; }
.empty { font-size: 8.4pt; color: #8a93a3; padding: 8pt 5pt; }

.doc-footer {
  margin-top: 16pt;
  padding-top: 8pt;
  border-top: 0.7pt solid #e8ebf0;
  font-size: 6.6pt;
  color: #8a93a3;
  font-family: 'DM Mono', ui-monospace, monospace;
  letter-spacing: 0.06em;
}
`;

/** Full standalone HTML document for one or more transcripts. */
export function buildTranscriptDocument(items) {
  const list = Array.isArray(items) ? items : [];
  const body = list
    .map((item, i) => renderTranscript(item, i === list.length - 1))
    .join('');
  const title = list.length === 1
    ? `Berwind-Chat-Transcript-${String(list[0]?.session?.sessionId || '').split('-')[0]}`
    : `Berwind-Chat-Transcripts-${list.length}`;

  return (
    '<!DOCTYPE html><html><head><meta charset="utf-8" />' +
    `<title>${escapeHtml(title)}</title>` +
    `<style>${STYLES}</style>` +
    `</head><body>${body}</body></html>`
  );
}

/**
 * Open the transcript(s) in a print window. The user picks "Save as PDF".
 * Returns false when a popup blocker prevented the window from opening.
 */
export function openTranscriptPrintWindow(items) {
  const html = buildTranscriptDocument(items);
  const win = window.open('', '_blank');
  if (!win) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  // Give webfonts a moment so the first paint isn't a fallback face.
  const trigger = () => {
    try {
      win.focus();
      win.print();
    } catch (_) {}
  };
  if (win.document.fonts && win.document.fonts.ready) {
    win.document.fonts.ready.then(() => setTimeout(trigger, 250)).catch(() => setTimeout(trigger, 700));
  } else {
    setTimeout(trigger, 700);
  }
  return true;
}
